import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';

import type { Company, ReferentMutation } from '../../../@types/Company';
import type { RootState } from '../../../store/store';
import { substituteReferentVariables } from '../../../utils/referentVariables';

/**
 * Context allowing callers (e.g. the per-referent PDF generator) to force the
 * `<Variable>` substitution to use a specific referent instead of the first
 * one. When `null`, callers fall back to the default behaviour (first valid
 * referent from the active company).
 */
export const ReferentOverrideContext = createContext<ReferentMutation | null>(null);

/**
 * Variable component that processes and displays text with variable replacements
 * Shows processed text in normal styling when variables are successfully replaced
 * Shows red highlighted text when variables remain unresolved
 */
export const Variable = ({ children }: { children: React.ReactNode }) => {
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const overrideReferent = useContext(ReferentOverrideContext);

  let textContent: string;
  if (React.isValidElement(children)) {
    textContent = String(children.props.children || '');
  } else if (Array.isArray(children)) {
    textContent = children.map(child => String(child)).join('');
  } else {
    textContent = String(children || '');
  }

  const processedText = substituteReferentVariables(textContent, company, overrideReferent);

  const hasVariables = processedText.includes('[data-');

  if (hasVariables) {
    return <span className="variable font-semibold text-slate-900">{processedText}</span>;
  }

  return <span className="font-semibold text-slate-900">{processedText}</span>;
};
